export type BulkActivationStatus = "pending" | "running" | "success" | "failed";

export type BulkActivationStep =
  | "idle"
  | "checkSim"
  | "saveBasket"
  | "addBasketEntry"
  | "setICCID"
  | "setAlias"
  | "addOption"
  | "setDevice"
  | "checkout";

export type BulkSimCsvRow = {
  iccid: string;
  puk: string;
  alias: string;
};

export type BulkSimRowState = BulkSimCsvRow & {
  status: BulkActivationStatus;
  step: BulkActivationStep;
  errorMessage?: string;
};

export const STEP_DELAY_MS = 1500;
export const ROW_DELAY_MS = 2000;
export const WAIT_TIMEOUT_MS = 15000;
export const WAIT_POLL_INTERVAL_MS = 500;
export const BASE_OPTION_PRODUCT_ID = 131001;

const normalizeHeader = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const findHeaderIndex = (
  headers: string[],
  matcher: (header: string) => boolean,
) => headers.findIndex(matcher);

const findIccidIndex = (headers: string[]) =>
  findHeaderIndex(
    headers,
    (header) => header === "iccid" || header.endsWith("iccid"),
  );

const findPukIndex = (headers: string[]) =>
  findHeaderIndex(
    headers,
    (header) => header === "puk" || header.startsWith("puk"),
  );

const findAliasIndex = (headers: string[]) =>
  findHeaderIndex(headers, (header) => header === "alias");

const normalizePuk = (value: string) => {
  const digits = value.replace(/\s+/g, "");
  if (/^\d{1,8}$/.test(digits)) {
    return digits.padStart(8, "0");
  }

  return digits;
};

const splitCsvLine = (line: string) => {
  const output: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      output.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  output.push(current.trim());

  return output;
};

export const parseBulkSimCsv = (csvText: string): BulkSimRowState[] => {
  const lines = csvText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return [];
  }

  const headerColumns = splitCsvLine(lines[0]).map(normalizeHeader);
  const iccidIndex = findIccidIndex(headerColumns);
  const pukIndex = findPukIndex(headerColumns);
  const aliasIndex = findAliasIndex(headerColumns);

  if (iccidIndex === -1 || pukIndex === -1 || aliasIndex === -1) {
    return [];
  }

  return lines.slice(1).map((line) => {
    const columns = splitCsvLine(line);
    const iccid = (columns[iccidIndex] ?? "").replace(/\s+/g, "");
    const puk = normalizePuk(columns[pukIndex] ?? "");
    const alias = (columns[aliasIndex] ?? "").trim();

    if (!iccid || !puk || !alias) {
      return {
        iccid,
        puk,
        alias,
        status: "failed",
        step: "idle",
        errorMessage: "Missing required values in CSV row",
      };
    }

    if (!/^\d{20}$/.test(iccid)) {
      return {
        iccid,
        puk,
        alias,
        status: "failed",
        step: "idle",
        errorMessage: "ICCID must contain exactly 20 digits",
      };
    }

    if (!/^\d{8}$/.test(puk)) {
      return {
        iccid,
        puk,
        alias,
        status: "failed",
        step: "idle",
        errorMessage: "PUK must contain exactly 8 digits",
      };
    }

    return {
      iccid,
      puk,
      alias,
      status: "pending",
      step: "idle",
    };
  });
};

export const setRowStep = (
  row: BulkSimRowState,
  step: BulkActivationStep,
): BulkSimRowState => ({
  ...row,
  step,
  status: "running",
  errorMessage: undefined,
});

export const setRowSuccess = (row: BulkSimRowState): BulkSimRowState => ({
  ...row,
  status: "success",
  step: "checkout",
  errorMessage: undefined,
});

export const setRowFailure = (
  row: BulkSimRowState,
  step: BulkActivationStep,
  errorMessage: string,
): BulkSimRowState => ({
  ...row,
  status: "failed",
  step,
  errorMessage,
});

export const getStepLabel = (step: BulkActivationStep) => {
  if (step === "idle") {
    return "queued";
  }
  if (step === "checkSim") {
    return "checking SIM";
  }
  if (step === "saveBasket") {
    return "getting basket";
  }
  if (step === "addBasketEntry") {
    return "adding basket entry";
  }
  if (step === "setICCID") {
    return "setting ICCID/PUK";
  }
  if (step === "setAlias") {
    return "setting alias";
  }
  if (step === "addOption") {
    return "adding option";
  }
  if (step === "setDevice") {
    return "setting device";
  }

  return "checkout";
};
